// Releases, pins and jobs (spec/dashboard.md section 12.2; R-BR-03, R-BR-04): three route tabs under
// `branches/{branch}/releases[/environments|/jobs]`. A release is `#<number>`, never a sha (Q-C-23). The next-step URLs:
// `releases?build=1` opens Build release…, `releases/environments?pin=<env>&release=<id>` opens that environment's Pin.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ops } from '../../api/operations';
import type { Page, Release, ReleasePin } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Chip } from '../../ui/Chip';
import { DataGrid, type Column } from '../../ui/DataGrid';
import { Switch } from '../../ui/inputs';
import { Modal, useUrlParam } from '../../ui/Modal';
import { Tabs } from '../../ui/Tabs';
import { formatInstant, useTimeZone, zoneLabel } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { formatBytes } from '../env/EnvironmentSettingsPage';
import { BuildDialog } from './BuildDialog';
import { EnvironmentCard, usePins } from './EnvironmentCard';
import { JobsTab } from './JobsTab';
import { asApiError, CATALOG_SIZE, countEntries, keys, pretty, RELEASES_PAGE_SIZE, shortSha } from './releases';
import './releases.css';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { EmptyState } from '../../ui/Banner';
import { GitBranch, Package, RefreshCw, Rocket, Server } from '../../ui/icons';

type TabKey = 'releases' | 'environments' | 'jobs';

export function ReleasesPage() {
  const { branch = '', tab } = useParams();
  const { org, game } = useGame();
  const current: TabKey = tab === 'environments' || tab === 'jobs' ? tab : 'releases';
  const items = (['releases', 'environments', 'jobs'] as const).map((key) => ({ key, label: key === 'releases' ? 'Releases' : key === 'environments' ? 'Environments' : 'Jobs', to: routes.releases(org, game.slug, branch, key) }));
  return (
    <section className="releases-page" data-testid="releases">
      <div className="page-head">
        <h1>Releases</h1>
        <span className="page-meta">
          <GitBranch className="icon-s" aria-hidden="true" />
          <code>{branch}</code>
        </span>
      </div>
      <Tabs items={items} value={current} ariaLabel="Releases sections" />
      {current === 'releases' && <ReleasesTab branch={branch} />}
      {current === 'environments' && <EnvironmentsTab />}
      {current === 'jobs' && <JobsTab />}
    </section>
  );
}

/** Every branch's releases, newest first: the Pin select and the `#N` lookups. */
export function useReleaseCatalog() {
  const { org, game } = useGame();
  const { client } = useAuth();
  return useQuery(keys.releasesCatalog(org, game.slug), () => client.data(ops.listReleases, { path: { org, game: game.slug }, query: { limit: CATALOG_SIZE } }) as Promise<Page<Release>>);
}

/** Reads one environment's active pins (the shared cache entry of its card) and hands them up. */
function PinsProbe({ env, onPins }: { env: string; onPins: (env: string, pins: ReleasePin[]) => void }) {
  const q = usePins(env);
  useEffect(() => {
    if (q.data) onPins(env, q.data.items ?? []);
  }, [q.data, env, onPins]);
  return null;
}

function ReleasesTab({ branch }: { branch: string }) {
  const { org, game, environments, can, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const [params, setParams] = useSearchParams();
  const manifest = useUrlParam('manifest');
  const navigate = useNavigate();
  // `?build=1` (the Save All and merge toasts' Build release, section 12.2) opens the Build dialog; closing it drops the param
  const [building, setBuilding] = useState(() => params.get('build') === '1');
  const closeBuild = () => {
    setBuilding(false);
    if (params.has('build')) {
      const next = new URLSearchParams(params);
      next.delete('build');
      setParams(next, { replace: true });
    }
  };
  const devEnv = environments.find((e) => e.kind === 'dev');
  const onBuilt = (release: Partial<Release>) => {
    const label = release.number !== undefined && release.number !== null ? `Release #${release.number} built` : 'Release built';
    const releaseId = release.releaseId;
    toasts.success(label, {
      actions:
        devEnv && releaseId
          ? [{ label: `Pin on ${devEnv.name ?? devEnv.slug}`, onClick: () => navigate(`${routes.releases(org, game.slug, branch, 'environments')}?pin=${encodeURIComponent(devEnv.slug)}&release=${encodeURIComponent(releaseId)}`) }]
          : undefined,
    });
  };
  const all = params.get('branches') === 'all';
  const filter = all ? null : branch;

  const fetchPage = (cursor: string | null) => client.data(ops.listReleases, { path: { org, game: game.slug }, query: { ...(filter ? { branch: filter } : {}), limit: RELEASES_PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as Promise<Page<Release>>;
  const listKey = keys.releasesList(org, game.slug, filter);
  const q = useQuery(listKey, () => fetchPage(null));
  const first = q.data;
  const [more, setMore] = useState<{ base: Page<Release>; items: Release[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstRef = useRef(first);
  firstRef.current = first;
  const extra = more && more.base === first ? more : null;
  const rows = [...(first?.items ?? []), ...(extra?.items ?? [])];
  const nextCursor = extra ? extra.cursor : (first?.nextCursor ?? null);
  const loadMore = async () => {
    if (!first || !nextCursor || loadingMore) return;
    const base = first;
    setLoadingMore(true);
    try {
      const page = await fetchPage(nextCursor);
      if (firstRef.current !== base) return;
      setMore({ base, items: [...(extra?.items ?? []), ...(page.items ?? [])], cursor: page.nextCursor ?? null });
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setLoadingMore(false);
    }
  };

  // where each release is the default: the active pins of every environment
  const [pins, setPins] = useState<Record<string, ReleasePin[]>>({});
  const onPins = useCallback((env: string, items: ReleasePin[]) => setPins((p) => (p[env] === items ? p : { ...p, [env]: items })), []);
  const pinnedOn = (releaseId: string) => environments.filter((e) => (pins[e.slug] ?? []).some((p) => p.isDefault && !p.revokedAt && !p.supersededById && p.releaseId === releaseId));

  // the grid re-renders its visible rows when the set of default pins moves
  const pinsRevision = Array.from(Object.entries(pins).flatMap(([env, items]) => items.filter((p) => p.isDefault && !p.revokedAt).map((p) => `${env}:${p.id}`)).join('|')).reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0);
  const canBuild = environments.some((e) => can('deploy', e.slug));
  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const columns: Column<Release>[] = [
    { key: 'number', header: '#', fixed: true, render: (r) => <strong className="mono">#{r.number}</strong>, cellText: (r) => `#${r.number}`, sortValue: (r) => Number(r.number) },
    { key: 'releaseId', header: 'Release id', render: (r) => <code className="small">{r.releaseId}</code>, cellText: (r) => r.releaseId },
    { key: 'branch', header: 'Branch', render: (r) => r.branchName, cellText: (r) => r.branchName },
    {
      key: 'commit',
      header: 'Commit',
      render: (r) => (
        <Link to={routes.history(org, game.slug, r.branchName, r.commit)} title={r.commit}>
          <code>{shortSha(r.commit)}</code>
        </Link>
      ),
      cellText: (r) => r.commit,
    },
    { key: 'builtBy', header: 'Built by', render: (r) => r.builtBy?.label ?? '—', cellText: (r) => r.builtBy?.label ?? '' },
    { key: 'created', header: 'Created', render: (r) => when(r.createdAt), cellText: (r) => when(r.createdAt), sortValue: (r) => r.createdAt ?? '' },
    { key: 'files', header: 'Files / bytes', align: 'right', render: (r) => `${r.fileCount} · ${formatBytes(r.totalBytes)}`, cellText: (r) => `${r.fileCount} · ${formatBytes(r.totalBytes)}` },
    {
      key: 'counts',
      header: 'Counts',
      render: (r) => (
        <span className="chips">
          {countEntries(r.counts).map(([k, v]) => (
            <Chip key={k}>
              {k}: {v}
            </Chip>
          ))}
        </span>
      ),
      cellText: (r) => countEntries(r.counts).map(([k, v]) => `${k}: ${v}`).join(', '),
    },
    { key: 'notes', header: 'Notes', render: (r) => r.notes ?? '', cellText: (r) => r.notes ?? '' },
    {
      key: 'pinned',
      header: 'Pinned on',
      render: (r) => (
        <span className="chips" data-testid={`pinned-on-${r.releaseId}`}>
          {pinnedOn(r.releaseId).map((e) => (
            <Chip key={e.slug} tone={e.kind === 'prod' ? 'danger' : e.kind === 'stage' ? 'warning' : 'info'}>
              {e.slug}
            </Chip>
          ))}
        </span>
      ),
      cellText: (r) => pinnedOn(r.releaseId).map((e) => e.slug).join(', '),
    },
    {
      key: 'manifest',
      header: '',
      render: (r) => (
        <Button size="s" variant="ghost" onClick={() => manifest.open(r.releaseId)} title={`The manifest of #${r.number}`}>
          Manifest
        </Button>
      ),
    },
  ];

  const toggleAll = (v: boolean) => {
    const next = new URLSearchParams(params);
    if (v) next.set('branches', 'all');
    else next.delete('branches');
    setParams(next, { replace: true });
  };
  const error = q.error ? asApiError(q.error) : null;
  return (
    <div data-testid="releases-tab">
      {environments.map((e) => (
        <PinsProbe key={e.slug} env={e.slug} onPins={onPins} />
      ))}
      {error && <LoadError>{error.message}</LoadError>}
      <DataGrid
        id="releases"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.releaseId}
        loading={q.loading && !first}
        revision={pinsRevision}
        emptyState={<EmptyState icon={<Rocket />} title={all ? 'No release built yet' : `No release of ${branch} yet`} body="A release freezes a commit of the branch into the files clients download; build one, then pin it to an environment." />}
        caption={
          <span>
            {rows.length} release{rows.length === 1 ? '' : 's'} {all ? 'of every branch' : `of ${branch}`} <span className="muted small">· times in {zoneLabel(zone, { offsets })}</span>
          </span>
        }
        toolbar={
          <>
            <Switch label="All branches" aria-label="All branches" checked={all} onChange={toggleAll} />
            <div className="spacer" />
            <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={() => queryCache.invalidate(keys.releasesPrefix(org, game.slug))} busy={q.loading && !!first}>
              Refresh
            </Button>
            <Button size="s" variant="primary" icon={<Package aria-hidden="true" />} onClick={() => setBuilding(true)} missingPermission={canBuild ? null : 'deploy'}>
              Build release…
            </Button>
          </>
        }
      />
      {nextCursor && (
        <div className="row releases-more">
          <Button size="s" data-testid="releases-load-more" onClick={() => void loadMore()} busy={loadingMore}>
            Load more
          </Button>
        </div>
      )}
      <BuildDialog branch={branch} open={building} onClose={closeBuild} onBuilt={onBuilt} missingPermission={canBuild ? null : 'deploy'} />
      <ManifestDrawer release={manifest.value} onClose={manifest.close} />
    </div>
  );
}

function ManifestDrawer({ release, onClose }: { release: string | null; onClose: () => void }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const q = useQuery(release ? keys.manifest(org, game.slug, release) : null, () => client.data(ops.getReleaseManifest, { path: { org, game: game.slug, release: release! } }));
  return (
    <Modal title={`Manifest · ${release ?? ''}`} open={!!release} onClose={onClose} drawer>
      <div data-testid="manifest-drawer">
        {q.error ? (
          <LoadError>{asApiError(q.error).message}</LoadError>
        ) : q.data === undefined ? (
          <SkeletonRows rows={6} label="Loading the manifest" />
        ) : (
          <pre className="json manifest-json">{pretty(q.data)}</pre>
        )}
      </div>
    </Modal>
  );
}

function EnvironmentsTab() {
  const { environments } = useGame();
  const [params] = useSearchParams();
  // `?pin=<env>&release=<releaseId>` (the built toast's Pin on <dev>, section 12.2) opens that environment's Pin dialog
  const pinEnv = params.get('pin');
  const pinRelease = params.get('release') ?? undefined;
  const catalog = useReleaseCatalog();
  const releases = [...(catalog.data?.items ?? [])].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '') || Number(b.number) - Number(a.number));
  return (
    <div data-testid="environments-tab" className="env-cards">
      {environments.length === 0 && <EmptyState icon={<Server />} title="This game has no environment." body="Every game is created with dev, stage and prod; ask an owner to restore them." />}
      {environments.map((env) => (
        <EnvironmentCard key={env.slug} env={env} releases={releases} autoOpen={pinEnv === env.slug ? { dialog: 'pin', releaseId: pinRelease } : undefined} />
      ))}
    </div>
  );
}
